import { useState } from "react";
import { toast } from "sonner";
import { Loader2, PenLine, Square } from "lucide-react";
import { API } from "@/lib/api";

const LENGTHS = [["court", "Court"], ["moyen", "Moyen"], ["long", "Long"]];

// "TITRE: …\nRÉSUMÉ: …\n---\nparagraphs" -> fields (works on partial text while streaming)
const parse = (raw) => {
  const [head, ...rest] = raw.split(/\n-{3,}\n?/);
  const title = head.match(/TITRE\s*:\s*(.*)/i)?.[1]?.trim() || "";
  const excerpt = head.match(/R[ÉE]SUM[ÉE]\s*:\s*([\s\S]*)/i)?.[1]?.trim() || "";
  return { title, excerpt, contentText: rest.join("\n").trim() };
};

// « Brouillon IA » : ChatGPT writes a Magazine article from the team's notes, streamed into the form
export const ArticleDraft = ({ onDraft }) => {
  const [brief, setBrief] = useState("");
  const [length, setLength] = useState("moyen");
  const [ctrl, setCtrl] = useState(null);

  const run = async () => {
    const controller = new AbortController();
    setCtrl(controller);
    let raw = "";
    try {
      const res = await fetch(`${API}/admin/news/draft`, {
        method: "POST", credentials: "include", signal: controller.signal,
        headers: { "Content-Type": "application/json" }, body: JSON.stringify({ brief, length }),
      });
      if (!res.ok || !res.body) throw new Error(res.status === 401 ? "Session expirée : reconnecte-toi." : "La rédaction a échoué.");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const events = buf.split("\n\n");
        buf = events.pop();
        for (const ev of events) {
          const msg = JSON.parse(ev.replace(/^data:\s*/, "") || "{}");
          if (msg.error) throw new Error(msg.error);
          if (msg.delta) { raw += msg.delta; onDraft(parse(raw)); }
        }
      }
      toast.success("Brouillon prêt : relis, complète les [à compléter], puis enregistre.");
    } catch (e) {
      if (e.name !== "AbortError") toast.error(e.message || "La rédaction a échoué.");
    } finally {
      setCtrl(null);
    }
  };

  return (
    <div className="border border-primary/60 bg-primary/10 p-4 space-y-3" data-testid="article-draft">
      <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-white">
        <PenLine className="w-4 h-4 text-primary" /> Brouillon avec l'IA
      </p>
      <p className="text-zinc-300 text-sm">Note les idées, infos et liens : l'IA rédige titre, résumé et texte. Rien n'est publié avant « Enregistrer ».</p>
      <textarea value={brief} onChange={(e) => setBrief(e.target.value)} rows={4} maxLength={4000} data-testid="article-draft-brief"
        placeholder="Ex : chronique de l'album X de Y, sorti chez Z, 10 titres, invités…, ce qu'on en pense…"
        className="w-full bg-transparent border border-white/30 focus:border-white text-white placeholder:text-zinc-400 p-3 outline-none" />
      <div className="flex flex-wrap items-center gap-2">
        {LENGTHS.map(([k, label]) => (
          <button key={k} type="button" onClick={() => setLength(k)} aria-pressed={length === k} data-testid={`article-draft-length-${k}`}
            className={`px-3 py-2 text-xs uppercase font-bold tracking-[0.15em] border transition-colors ${length === k ? "bg-white text-black border-white" : "border-white/30 text-white hover:border-white"}`}>
            {label}
          </button>
        ))}
        {ctrl ? (
          <button type="button" onClick={() => ctrl.abort()} data-testid="article-draft-stop"
            className="ml-auto inline-flex items-center gap-2 border border-white text-white uppercase text-xs font-bold tracking-[0.15em] px-4 py-2 hover:bg-white hover:text-black transition-colors">
            <Loader2 className="w-4 h-4 animate-spin" /> Rédaction… <Square className="w-3 h-3" />
          </button>
        ) : (
          <button type="button" onClick={run} disabled={brief.trim().length < 10} data-testid="article-draft-run"
            className="ml-auto bg-white text-black uppercase text-xs font-bold tracking-[0.15em] px-4 py-2 hover:bg-primary hover:text-white transition-colors disabled:opacity-50">
            Rédiger
          </button>
        )}
      </div>
    </div>
  );
};
